import * as Sentry from "@sentry/react";
import { useQuery } from "react-query";

import axios from "axios";

import {
  TypeWellPerformanceRequestSchema,
  TypeWellPerformanceRequestSchemaType,
  TypeWellPerformanceResponseSchema,
  TypeWellPerformanceResponseSchemaType
} from "models/schemas/typeWellPerformance.schema";

const DATA_RS_ROOT = process.env.REACT_APP_DATA_RS_ROOT;
const ENDPOINT = `${DATA_RS_ROOT}/api/v1/data/type-wells/performance`;

/**
 * Query key for type well performance data
 * Used for cache invalidation after type well saves
 */
export const TYPE_WELL_PERFORMANCE_QUERY_KEY = "typeWellPerformance";

/**
 * Fetch function for type well performance data
 */
async function fetchTypeWellPerformance(
  request: TypeWellPerformanceRequestSchemaType
): Promise<TypeWellPerformanceResponseSchemaType> {
  const validatedRequest = TypeWellPerformanceRequestSchema.parse(request);

  try {
    const { data } = await axios.post(ENDPOINT, validatedRequest);
    return TypeWellPerformanceResponseSchema.parse(data);
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
}

/**
 * React Query hook for fetching type well performance data
 */
export function useTypeWellPerformanceQuery(
  request: TypeWellPerformanceRequestSchemaType | null,
  enabled: boolean = true
) {
  const queryKey = request
    ? [
        TYPE_WELL_PERFORMANCE_QUERY_KEY,
        {
          typeWellIds: [...request.typeWellIds].sort(), // Sort for consistent key
          fields: [...request.fields].sort()
        }
      ]
    : [TYPE_WELL_PERFORMANCE_QUERY_KEY, null];

  return useQuery<TypeWellPerformanceResponseSchemaType, Error>(
    queryKey,
    () => {
      if (!request) {
        throw new Error("Request is required");
      }
      return fetchTypeWellPerformance(request);
    },
    {
      enabled: enabled && request !== null && request.typeWellIds.length > 0,
      staleTime: 0,
      cacheTime: 30000,
      retry: 1,
      refetchOnWindowFocus: false
    }
  );
}
